/**
 * SectionHeadingDot — b15c C1.
 *
 * A single, shared per-section dot indicator that sits at the start of a
 * section heading row. It is the heading-row counterpart to the decorative
 * left-margin {@link DotRail}: where DotRail runs a vertical rail down the
 * page, this marks each individual section heading with one paired dot so the
 * "line + dots" language is consistent on every page (Profile, Pokedex, Trade,
 * Calendar, Friends, Scan/Live Scan, Settings).
 *
 * Geometry is fixed here — same dot size, same glow, same gap — so every
 * heading across every page lines up identically with Profile's spine dot.
 * Drop it in as the first child of a heading row:
 *
 *   <h3 className="… inline-flex items-center gap-1.5">
 *     <SectionHeadingDot /> Upcoming events
 *   </h3>
 *
 * b16 item 9: harmonised to the canonical Profile dot (b15c / PR #190) —
 * 8px and --dot-accent, so every section dot is visually identical to
 * Profile's spine dot in size, colour, and light/dark behaviour. For
 * single-line heading rows it stays centred on the text via `align-middle`;
 * for multi-line info blocks pass `topAlign` to hug the top of the block
 * (matching Profile's spine dot, which sits at the top of each rectangle).
 *
 * Colour is driven 100% by the canonical --dot-accent token (tokens.css): deep
 * green in light, bright neon-emerald in dark. No green hex is ever hard-coded.
 * The dot is aria-hidden so it never affects the accessible heading text.
 */
interface SectionHeadingDotProps {
  /**
   * Optional size override in px. Defaults to 8px — the canonical Profile
   * spine-dot size, so every section dot is visually identical to Profile's.
   */
  size?: number;
  /**
   * Align the dot with the TOP of the row instead of the text centre. Use for
   * multi-line heading/info blocks so the dot hugs the top like Profile's.
   */
  topAlign?: boolean;
  className?: string;
}

/**
 * SpineDot — the gutter/spine dot for info blocks that live inside a `pl-5`
 * spine gutter (a DotRail page or Profile's spine) but can't receive the
 * automatic dot because they sit behind a component boundary. Render it as the
 * first child of a `position: relative` block: it pulls back into the 20px
 * gutter (left:-18) and straddles the rail line at top-2, matching
 * DotRail.withDot and Profile's SpineSection exactly. Colour is the canonical
 * --dot-accent token (no hard-coded green); aria-hidden so it never affects the
 * accessible heading.
 */
export function SpineDot({ className = "" }: { className?: string }) {
  return (
    <span
      aria-hidden
      data-testid="spine-dot"
      className={`pointer-events-none absolute top-2 z-10 w-2 h-2 rounded-full ${className}`}
      style={{ left: -18, background: "var(--dot-accent)", boxShadow: "0 0 6px var(--dot-accent)" }}
    />
  );
}

export function SectionHeadingDot({ size = 8, topAlign = false, className = "" }: SectionHeadingDotProps) {
  return (
    <span
      aria-hidden
      data-testid="section-heading-dot"
      className={`inline-block shrink-0 rounded-full ${topAlign ? "self-start align-top" : "align-middle"} ${className}`}
      style={{
        width: size,
        height: size,
        ...(topAlign ? { marginTop: "0.2em" } : null),
        background: "var(--dot-accent)",
        boxShadow: "0 0 6px var(--dot-accent)",
      }}
    />
  );
}
